import { apiFetch } from './apiFetch';

export interface BoatDatePrice {
  id: string;
  boat_id: string;
  date: string;
  price: number;
  rule_prices?: Record<string, number>;
  status: 'AVAILABLE' | 'NOT_AVAILABLE' | 'CLOSED' | 'HALF_MORNING' | 'HALF_AFTERNOON';
  isManualOverride?: boolean;
  appliedRuleId?: string | null;
  priceSource?: 'manual' | 'rule_auto' | 'bulk_operation';
  created_at?: string;
  updated_at?: string;
}

export interface PriceChangeLogEntry {
  id: string;
  boat_id: string;
  date: string;
  old_price: number | null;
  new_price: number;
  changed_by: string | null;
  change_source: string;
  change_details: Record<string, any> | null;
  created_at: string;
  changedByUser?: { id: string; first_name: string; last_name: string; email: string };
  boat?: { id: string; name: string };
}

export const boatDatePriceService = {
  async getForBoat(
    boatId: string,
    startDate?: string,
    endDate?: string,
    options?: { withMarkup?: boolean },
  ): Promise<{ success: boolean; data: any }> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (options?.withMarkup) params.append('with_markup', '1');

    return apiFetch(`/boats/${boatId}/date-prices?${params}`, { cache: 'no-store' });
  },

  async upsert(boatId: string, data: { date: string; price: number; rule_prices?: Record<string, number>; status?: string }): Promise<{ success: boolean; data: BoatDatePrice }> {
    return apiFetch(`/boats/${boatId}/date-prices`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async bulkUpsert(boatId: string, dates: Array<{ date: string; price: number; rule_prices?: Record<string, number>; status?: string }>): Promise<{ success: boolean; count: number }> {
    return apiFetch(`/boats/${boatId}/date-prices/bulk`, {
      method: 'POST',
      body: JSON.stringify({ dates }),
    });
  },

  async delete(boatId: string, date: string): Promise<void> {
    return apiFetch(`/boats/${boatId}/date-prices/${date}`, {
      method: 'DELETE',
    });
  },

  async adjustPrices(boatId: string, data: {
    start_date: string;
    end_date: string;
    adjustment_type: 'percentage' | 'absolute';
    adjustment_value: number;
  }): Promise<{ success: boolean; count: number }> {
    return apiFetch(`/boats/${boatId}/date-prices/adjust`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async copyPrices(boatId: string, data: {
    source_start: string;
    source_end: string;
    target_start: string;
  }): Promise<{ success: boolean; count: number }> {
    return apiFetch(`/boats/${boatId}/date-prices/copy`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getPriceHistory(boatId: string, params?: { start_date?: string; end_date?: string; page?: number; limit?: number }): Promise<{
    success: boolean;
    data: PriceChangeLogEntry[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));

    return apiFetch(`/boats/${boatId}/price-history?${searchParams}`);
  },
};
